// material-ui
import {
    Autocomplete,
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';

// third party
import { useFormik } from 'formik';
import * as yup from 'yup';

// project imports
import AnimateButton from 'ui-component/components/AnimateButton';
import { useDispatch, useSelector } from 'store';
import { FormattedMessage, useIntl } from "react-intl";
import React, { useEffect, useState } from "react";
import { getAllMembers } from "../../../../../store/actions/member.actions";
import { User } from "../../../../../types/type/users-types";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LoadingButton } from "@mui/lab";
import Chip from "../../../../../ui-component/components/Chip";
import { useTheme } from "@mui/styles";
import { addTransaction } from "../../../../../store/actions/transaction.actions";

interface Props {
    handleDrawerChangeState: () => void;
}

// ==============================|| MEMBERS - FORM FOR EDIT/VIEW ||============================== //

interface Props {
    handleDrawerChangeState: () => void;
    isDeleteEnabled?: boolean;
    setSuccessResult: any;
    setRechargedMemberId: any;
    memberId: string | undefined;
}

const RechargeForm = ({
                          handleDrawerChangeState,
                          isDeleteEnabled,
                          setSuccessResult,
                          setRechargedMemberId,
                          memberId
                      }: Props) => {

    const dispatch = useDispatch();
    const intl = useIntl()
    const theme = useTheme();
    const [dataMember, setDataMember] = useState<User[]>([]);
    const { member } = useSelector((state) => state.member);
    const { loadingOperation } = useSelector((state) => state.loading);

    useEffect(() => {
        const init = async () => {
            await dispatch(getAllMembers());
        }
        init()
    }, [])

    const [dataFirm, setDataFirm] = useState<User[]>([]);
    const { firm } = useSelector((state) => state.firm);

    useEffect(() => {
        setDataFirm(firm);
    }, [firm])

    const validationSchema = yup.object({
        affiliate_id: yup.string().required(intl.formatMessage({ id: 'FRM00077' })),
        transaction_value: yup.string().required(intl.formatMessage({ id: 'FRM00083' })),
        firm: yup.string().required(intl.formatMessage({ id: 'FRM00085' })),
    });

    const formikRecharge = useFormik({
        enableReinitialize: false,
        validationSchema,
        initialValues: {
            affiliate_id: '',
            transaction_desc: 'Ricarica borsellino',
            transaction_type: 3,
            transaction_date: format(new Date(), 'yyyy-MM-dd HH:mm'),
            transaction_value: '',
            payment_method: 1,
            firm: '',
            note: '',
        },
        onSubmit: async (values) => {
            await dispatch(addTransaction(values));
            setRechargedMemberId(formikRecharge.values.affiliate_id);
            setSuccessResult(true);
            formikRecharge.resetForm()
        }
    });

    useEffect(() => {
        setDataMember(member);
        if (memberId) {
            const localMember = member.find((el) => el.id == memberId)
            formikRecharge.setFieldValue('affiliate_id', localMember?.id);
            formikRecharge.setFieldValue('firm', localMember?.firm_id);
        }
    }, [member])

    return (
        <>
            <form onSubmit={formikRecharge.handleSubmit}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Grid container justifyContent="flex-start">
                            <Autocomplete
                                id="affiliate_id"
                                value={
                                    dataMember.find((el) => el.id === formikRecharge.values.affiliate_id) ||
                                    null
                                }
                                onChange={(event, value) => {
                                    formikRecharge.setFieldValue('affiliate_id', value?.id);
                                    formikRecharge.setFieldValue('firm', value?.firm_id);
                                }}
                                options={dataMember}
                                fullWidth
                                autoHighlight
                                disabled={loadingOperation}
                                getOptionLabel={(option) => option.name + ' ' + option.surname}
                                isOptionEqualToValue={(option) => option.id === formikRecharge.values.affiliate_id}
                                renderOption={(props, option) => (
                                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                        {option.name + ' ' + option.surname}
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={intl.formatMessage({ id: 'FRM00055' })}
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'one-time-code' // disable autocomplete and autofill
                                        }}
                                        error={formikRecharge.touched.affiliate_id && Boolean(formikRecharge.errors.affiliate_id)}
                                    />
                                )}
                            />
                            {formikRecharge.errors.affiliate_id && formikRecharge.touched.affiliate_id &&
                                <FormHelperText error>{formikRecharge.errors.affiliate_id}</FormHelperText>}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle1"><FormattedMessage id={'ACC00020'} /></Typography>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                                    <DateTimePicker
                                        InputProps={{
                                            id: "transaction_date",
                                            name: "transaction_date"
                                        }}
                                        disabled={loadingOperation}
                                        inputFormat={'dd-MM-yyyy HH:mm'}
                                        renderInput={(props) => <TextField fullWidth {...props} />}
                                        value={formikRecharge.values.transaction_date}
                                        onChange={(value) => {
                                            if (value) {
                                                formikRecharge.setFieldValue('transaction_date', format(new Date(value), 'yyyy-MM-dd HH:mm'))
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle1"><FormattedMessage id={'FRM00049'} /></Typography>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    id="transaction_value"
                                    name="transaction_value"
                                    type={'number'}
                                    disabled={loadingOperation}
                                    label={intl.formatMessage({ id: 'FRM00050' })}
                                    value={formikRecharge.values.transaction_value}
                                    onChange={formikRecharge.handleChange}
                                    error={formikRecharge.touched.transaction_value && Boolean(formikRecharge.errors.transaction_value)}
                                    helperText={formikRecharge.touched.transaction_value && formikRecharge.errors.transaction_value}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="start">€</InputAdornment>
                                        )
                                    }}
                                />
                                <Stack direction={'row'} style={{ marginTop: '8px' }} justifyContent={'space-between'}>
                                    <Chip label={'20 €'} size={'small'}
                                          onClick={() => formikRecharge.setFieldValue('transaction_value', 20)} />
                                    <Chip label={'50 €'} size={'small'}
                                          onClick={() => formikRecharge.setFieldValue('transaction_value', 50)} />
                                    <Chip label={'100 €'} size={'small'}
                                          onClick={() => formikRecharge.setFieldValue('transaction_value', 100)} />
                                    <Chip label={'150 €'} size={'small'}
                                          onClick={() => formikRecharge.setFieldValue('transaction_value', 150)} />
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle1"><FormattedMessage id={'FRM00076'} /></Typography>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <FormControl fullWidth>
                                    <Select
                                        id="payment_method"
                                        name="payment_method"
                                        displayEmpty
                                        disabled={loadingOperation}
                                        value={formikRecharge.values.payment_method}
                                        onChange={formikRecharge.handleChange}
                                        inputProps={{ 'aria-label': 'Without label' }}
                                        error={formikRecharge.touched.payment_method && Boolean(formikRecharge.errors.payment_method)}
                                    >
                                        <MenuItem key={1} value={'1'}>
                                            Satispay
                                        </MenuItem>
                                        <MenuItem key={1} value={'2'}>
                                            Bonifico
                                        </MenuItem>
                                        <MenuItem key={1} value={'3'}>
                                            Contanti
                                        </MenuItem>
                                        <MenuItem key={1} value={'4'}>
                                            Assegno
                                        </MenuItem>
                                        <MenuItem key={1} value={'5'}>
                                            Altro
                                        </MenuItem>
                                    </Select>
                                    {formikRecharge.errors.payment_method && formikRecharge.touched.payment_method &&
                                        <FormHelperText error>{formikRecharge.errors.payment_method}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle1"><FormattedMessage id={'ACC00017'} /></Typography>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <Grid container justifyContent="flex-start">
                                    <Autocomplete
                                        id="firm"
                                        onChange={(event, value) => {
                                            formikRecharge.setFieldValue('firm', value?.id);
                                        }}
                                        value={
                                            firm.find((el) => el.id === formikRecharge.values.firm) ||
                                            null
                                        }
                                        options={dataFirm}
                                        fullWidth
                                        autoHighlight
                                        disabled={loadingOperation}
                                        getOptionLabel={(option) => option.name + ''}
                                        isOptionEqualToValue={(option) => option.id === formikRecharge.values.firm}
                                        renderOption={(props, option) => (
                                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                {option.name}
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={intl.formatMessage({ id: 'ACC00018' })}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: 'one-time-code' // disable autocomplete and autofill
                                                }}
                                                error={formikRecharge.touched.firm && Boolean(formikRecharge.errors.firm)}
                                            />
                                        )}
                                    />
                                    {formikRecharge.errors.firm && formikRecharge.touched.firm &&
                                        <FormHelperText error>{formikRecharge.errors.firm}</FormHelperText>}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider>
                            <span style={{ color: theme.palette.grey[500] }}>Opzionali</span>
                        </Divider>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            id="note"
                            name="note"
                            value={formikRecharge.values.note}
                            disabled={loadingOperation}
                            label={intl.formatMessage({ id: 'FRM00019' })}
                            onChange={formikRecharge.handleChange}
                            error={formikRecharge.touched.note && Boolean(formikRecharge.errors.note)}
                            helperText={formikRecharge.touched.note && formikRecharge.errors.note}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} mt={3}>

                    </Grid>
                    <Grid item xs={12} md={6} mt={3}>
                        <Stack direction={'row'} textAlign={'right'} spacing={3}>
                            <AnimateButton>
                                <Button fullWidth variant="text" color={'error'} onClick={handleDrawerChangeState}
                                        disabled={loadingOperation}>
                                    <FormattedMessage id={'SYS00003'} />
                                </Button>
                            </AnimateButton>
                            <AnimateButton>
                                <LoadingButton fullWidth variant="contained" type="submit" loading={loadingOperation}>
                                    <FormattedMessage id={'FRM00007'} />
                                </LoadingButton>
                            </AnimateButton>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </>
    );
};
export default RechargeForm;